import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import { supabase } from "./supabase";

const categories = [
  "Jobs & Opportunity",
  "Infrastructure",
  "Higher Education",
  "Housing",
  "Agriculture",
  "Arts & Culture",
  "Water & Sanitation",
  "Science & Innovation",
];

const provinces = ["Gauteng", "Western Cape", "KwaZulu-Natal", "Eastern Cape", "Limpopo", "Mpumalanga", "North West", "Free State", "Northern Cape"];

const inputStyle = {
  width: "100%",
  background: "#F5F0E8",
  border: "1.5px solid #EDE8DF",
  borderRadius: 8,
  padding: "11px 14px",
  color: "#1A1A2E",
  fontSize: 16,
  fontFamily: "var(--font-body)",
  outline: "none",
  marginBottom: 16,
  boxSizing: "border-box",
};

const labelStyle = {
  fontSize: 11,
  color: "#888",
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  marginBottom: 6,
  display: "block",
};

export default function ProposeProject() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [form, setForm] = useState({ title: "", category: "", location: "", description: "", goal: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => setUser(user));
  }, []);

  const handleSubmit = async () => {
    if (!form.title || !form.category || !form.location || !form.description || !form.goal) {
      setError("Please fill in all fields.");
      return;
    }
    if (form.description.length < 20) {
      setError("Please write at least 20 characters describing the project.");
      return;
    }
    const goalNumber = Number(form.goal);
    if (!goalNumber || goalNumber <= 0) {
      setError("Please enter a valid funding goal.");
      return;
    }

    setLoading(true);
    setError("");
    const { error: submitError } = await supabase.from("projects").insert({
      title: form.title,
      category: form.category,
      location: form.location,
      description: form.description,
      goal: goalNumber,
      raised: 0,
      status: "pending_review",
      created_by: user.id,
    });
    setLoading(false);

    if (submitError) {
      setError(submitError.message);
    } else {
      setSuccess(true);
    }
  };

  if (success) {
    return (
      <div style={{ fontFamily: "var(--font-body)", background: "#F5F0E8", minHeight: "100vh", color: "#1A1A2E" }}>
        <Navbar />
        <div style={{ maxWidth: 480, margin: "0 auto", padding: "80px 24px", textAlign: "center" }}>
          <div style={{ width: 56, height: 56, background: "rgba(46,125,50,0.08)", borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#2E7D32" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <h2 style={{ fontSize: 20, fontWeight: 900, marginBottom: 10 }}>Your project has been submitted</h2>
          <p style={{ fontSize: 14, color: "#888", lineHeight: 1.7, marginBottom: 24 }}>
            It's now under review. You can check its status any time from your dashboard.
          </p>
          <button
            onClick={() => navigate("/dashboard")}
            style={{ background: "#1B3A6B", border: "none", borderRadius: 99, padding: "13px 28px", color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "var(--font-body)" }}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "var(--font-body)", background: "#F5F0E8", minHeight: "100vh", color: "#1A1A2E" }}>

      <style>{`
        .propose-header { padding: 56px 48px 40px; }
        .propose-wrap { max-width: 560px; margin: 0 auto; padding: 0 48px 72px; }
        .propose-card { background: #fff; border: 1px solid #EDE8DF; border-radius: 20px; padding: 32px; }
        @media (max-width: 768px) {
          .propose-header { padding: 36px 20px 28px; }
          .propose-wrap { padding: 0 20px 48px; }
          .propose-card { padding: 22px; }
        }
      `}</style>

      <Navbar />

      <div className="propose-header" style={{ background: "#fff", borderBottom: "1px solid #EDE8DF", textAlign: "center" }}>
        <div style={{ display: "inline-block", background: "rgba(27,58,107,0.07)", border: "1px solid rgba(27,58,107,0.15)", borderRadius: 99, padding: "5px 16px", fontSize: 11, color: "#1B3A6B", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 20 }}>
          Propose a Project
        </div>
        <h1 style={{ fontSize: "clamp(24px, 4vw, 36px)", fontWeight: 900, margin: "0 0 12px", letterSpacing: "-0.02em" }}>
          Tell us what your community needs
        </h1>
        <p style={{ fontSize: 14, color: "#888", lineHeight: 1.7, maxWidth: 460, margin: "0 auto" }}>
          Every proposal goes through a review before it goes live. Take your time and be specific, it helps your project move faster.
        </p>
      </div>

      <div className="propose-wrap">
        <div className="propose-card" style={{ marginTop: 32 }}>
          <label style={labelStyle}>Project title</label>
          <input style={inputStyle} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Community Borehole, Limpopo" />

          <label style={labelStyle}>Category</label>
          <select style={inputStyle} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            <option value="">Select category</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>

          <label style={labelStyle}>Province</label>
          <select style={inputStyle} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}>
            <option value="">Select province</option>
            {provinces.map(p => <option key={p} value={p}>{p}</option>)}
          </select>

          <label style={labelStyle}>Funding goal (R)</label>
          <input
            type="number"
            style={inputStyle}
            value={form.goal}
            onChange={(e) => setForm({ ...form, goal: e.target.value })}
            placeholder="e.g. 50000"
          />

          <label style={labelStyle}>Describe the project</label>
          <textarea
            style={{ ...inputStyle, resize: "vertical" }}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="What problem does this solve, and how will the funds be used?"
            rows={5}
          />
          <div style={{ fontSize: 11, color: "#888", marginTop: -12, marginBottom: 16 }}>{form.description.length} characters</div>

          {error && (
            <div style={{ background: "rgba(192,57,43,0.07)", border: "1px solid rgba(192,57,43,0.2)", borderRadius: 8, padding: "12px 16px", marginBottom: 16, fontSize: 13, color: "#C0392B" }}>
              {error}
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{ width: "100%", background: loading ? "#EDE8DF" : "#1B3A6B", border: "none", borderRadius: 99, padding: "14px", color: loading ? "#888" : "#fff", fontWeight: 800, fontSize: 15, cursor: loading ? "not-allowed" : "pointer", fontFamily: "var(--font-body)" }}
          >
            {loading ? "Submitting..." : "Submit for Review"}
          </button>
        </div>
      </div>

    </div>
  );
}